import { observer } from 'mobx-react-lite'
import { EditPencil, Plus as PlusIcon, Trash } from 'iconoir-react'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { useSearchParams } from 'react-router-dom'
import { debounce } from 'lodash'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { Button } from 'app/components/ui/button'
import { EmptyState } from 'app/components/ui/empty-state'
import { Input } from 'app/components/ui/input'
import { PageScroll } from 'app/components/layout/PageScroll'
import { TableBody, TableCell, TableRow } from 'app/components/ui/table'
import {
  GROUPED_TABLE_CELL,
  GroupedTable,
  GroupedTableHeaderCard,
  GroupedTableSection,
  type GroupedTableColumn,
} from 'app/components/table/GroupedTable'
import { TooltipWrapper } from 'app/components/ui/tooltip-wrapper'
import { useHandleSearchParams } from 'app/utils'
import { AdPlatformMappingType } from 'lib/adPlatformDefaults'
import { cn } from 'app/lib/utils'
import { GroupedPlatforms } from './useMappingPlatforms'

export type MappingPlatformsShadcnComponentProps = {
  groupedPlatforms: GroupedPlatforms
  platformNames: string[]
  loading: boolean
  canEditMapping: boolean
  onAdd: () => void
  onEdit: (platform: AdPlatformMappingType) => void
  onDelete: (id: string, platform: string) => Promise<void>
  isDefaultMapping: (platform: AdPlatformMappingType) => boolean
}

function PlatformSearch() {
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [searchValue, setSearchValue] = useState(searchParams.get(`query`) ?? ``)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setSearchValue(searchParams.get(`query`) ?? ``)
  }, [searchParams])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.value?.length && e?.target?.value?.length < 3) return
    handleSearchParams({ query: e.target.value })
  }

  return (
    <div className="relative w-[250px]">
      <MagnifyingGlassIcon className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
      <Input
        ref={input}
        type="search"
        placeholder="Search"
        defaultValue={searchValue}
        onChange={debounce(handleSearchChange, 250)}
        autoComplete="off"
        className="pl-8 text-sm"
      />
    </div>
  )
}

function platformColumns(canEditMapping: boolean): GroupedTableColumn[] {
  return [
    { label: `Source` },
    { label: `Medium` },
    ...(canEditMapping
      ? [{ label: `Actions`, colClassName: `w-[100px]`, headClassName: `text-right` } as GroupedTableColumn]
      : []),
  ]
}

function PlatformGroup({
  name,
  items,
  canEditMapping,
  onEdit,
  onDelete,
  isDefaultMapping,
}: {
  name: string
  items: AdPlatformMappingType[]
  canEditMapping: boolean
  onEdit: (platform: AdPlatformMappingType) => void
  onDelete: (id: string, platform: string) => Promise<void>
  isDefaultMapping: (platform: AdPlatformMappingType) => boolean
}) {
  return (
    <GroupedTableSection label={name} count={items.length}>
      <GroupedTable columns={platformColumns(canEditMapping)}>
        <TableBody className="divide-y divide-subtle">
          {items.map((item, idx) => {
            const isDefault = isDefaultMapping(item)
            return (
              <TableRow key={item.id ?? `${item.source}-${item.medium}-${idx}`}>
                <TableCell className={cn(GROUPED_TABLE_CELL, `text-strong`)}>{item.source}</TableCell>
                <TableCell className={cn(GROUPED_TABLE_CELL, `text-default`)}>{item.medium}</TableCell>
                {canEditMapping && (
                  <TableCell className={GROUPED_TABLE_CELL}>
                    <div className="flex justify-end gap-2">
                      <TooltipWrapper message={isDefault ? `System mapping cannot be updated` : `Update mapping`}>
                        <EditPencil
                          className={cn(
                            `h-5 w-5`,
                            isDefault ? `cursor-default text-minimal` : `cursor-pointer text-default hover:text-strong`,
                          )}
                          onClick={isDefault ? undefined : () => onEdit(item)}
                        />
                      </TooltipWrapper>
                      <TooltipWrapper message={isDefault ? `System mapping cannot be deleted` : `Delete mapping`}>
                        <Trash
                          className={cn(
                            `h-5 w-5`,
                            isDefault
                              ? `cursor-default text-minimal`
                              : `cursor-pointer text-default hover:text-red-600`,
                          )}
                          onClick={isDefault ? undefined : async () => await onDelete(item.id!, item.platform!)}
                        />
                      </TooltipWrapper>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </GroupedTable>
    </GroupedTableSection>
  )
}

export const MappingPlatformsShadcnComponent = observer(function MappingPlatformsShadcnComponent({
  groupedPlatforms,
  platformNames,
  loading,
  canEditMapping,
  onAdd,
  onEdit,
  onDelete,
  isDefaultMapping,
}: MappingPlatformsShadcnComponentProps): ReactElement {
  return (
    // Top padding lives on the content, not the scroller: padding on the scroll
    // container would inset the sticky header card and let rows peek above it.
    <PageScroll contentClassName="pb-6">
      <div className="mb-0.5 flex items-center gap-x-2 pt-6">
        <PlatformSearch />
        {canEditMapping && (
          <Button variant="primary" size="sm" onClick={onAdd}>
            <PlusIcon className="h-4 w-4" />
            <span>New Mapping</span>
          </Button>
        )}
      </div>
      <div className="mt-6 flex flex-col gap-5">
        {loading ? (
          <div className="py-8 text-center text-sm text-subtle">Loading…</div>
        ) : platformNames.length === 0 ? (
          <EmptyState
            title="No platform mappings"
            description="Mappings group source and medium values under an ad platform for reporting."
            action={
              canEditMapping ? (
                <Button variant="primary" size="sm" onClick={onAdd}>
                  <PlusIcon className="h-4 w-4" />
                  <span>New Mapping</span>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <GroupedTableHeaderCard columns={platformColumns(canEditMapping)} />
            {platformNames.map((name) => (
              <PlatformGroup
                key={name}
                name={name}
                items={groupedPlatforms[name] ?? []}
                canEditMapping={canEditMapping}
                onEdit={onEdit}
                onDelete={onDelete}
                isDefaultMapping={isDefaultMapping}
              />
            ))}
          </>
        )}
      </div>
    </PageScroll>
  )
})
